'use client';

import type { MouseEvent } from 'react';
import {
  ArrowUUpLeftIcon,
  BarcodeIcon,
  CalendarIcon,
  CaretDownIcon,
  CaretRightIcon,
  CreditCardIcon,
  InfoIcon,
  QrCodeIcon,
  ReceiptIcon,
  RepeatIcon,
  ArrowsLeftRightIcon,
  BankIcon,
  WalletIcon,
  TagIcon,
  HashIcon,
  PencilSimpleIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { SpaceSendAction, SpaceTransactionMarker } from '@/components/spaces/SpaceSendAction';
import { transactionDate } from '@/lib/transactions/dates';
import type { PaymentType } from '@/lib/transactions/types';
import {
  getRefundState,
  hasReportingAmount,
  isFlowNeutral,
  reportingAmount,
  uncoveredAmount,
} from '@/lib/transactions/refunds';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TagOverflowPill, TagPill } from '@/components/transactions/TagPill';
import { MAX_ROW_TAG_PILLS } from '@/lib/transactions/tags';
import { CategoryTile } from '@/components/CategoryTile';
import {
  TRANSACTION_LINE_INTERACTIVE,
  TransactionAmount,
  TransactionCategoryLabel,
  TransactionLine,
  TransactionMeta,
  TransactionMetaItem,
  TransactionTitle,
} from '@/components/transactions/TransactionLine';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { FaturaAffiliationPill } from './FaturaAffiliationPill';
import { RefundPill } from './RefundPill';
import {
  type CategoryIndex,
  getCategoryMeta,
} from './categoryIndex';
import type { ScreenFatura, ScreenTransaction } from './types';
import { cn } from '@/lib/utils';

interface Props {
  tx: ScreenTransaction;
  expanded: boolean;
  onToggle: () => void;
  onOpenDetails: (txId: string) => void;
  faturaById?: ReadonlyMap<string, ScreenFatura>;
  onOpenFatura?: (faturaId: string) => void;
  onOpenCategoryPicker?: (txId: string) => void;
  /** Opens the tag assignment drawer (VER-378). */
  onOpenTagPicker?: (txId: string) => void;
  /** Opens the detail drawer already in rename mode (VER-377). */
  onRename?: (txId: string) => void;
  onMarkRecurring?: (txId: string) => void;
  onOpenRefundLink?: (txId: string) => void;
  isCategorySaving?: boolean;
  isTagSaving?: boolean;
  isRecurringSaving?: boolean;
  isRefundSaving?: boolean;
  categoryIndex: CategoryIndex;
}

const PAYMENT_LABELS: Record<string, string> = {
  pix: 'Pix',
  credit: 'Crédito',
  debit: 'Débito',
  transfer: 'TED / DOC',
  boleto: 'Boleto',
  other: 'Outros',
};

const PAYMENT_ICONS: Record<PaymentType, React.ComponentType<{ className?: string; weight?: 'duotone' | 'regular' | 'bold' }>> = {
  pix: QrCodeIcon,
  credit: CreditCardIcon,
  debit: WalletIcon,
  transfer: ArrowsLeftRightIcon,
  boleto: BarcodeIcon,
  other: ReceiptIcon,
};

export function TransactionRow({
  tx,
  expanded,
  onToggle,
  onOpenDetails,
  faturaById,
  onOpenFatura,
  onOpenCategoryPicker,
  onOpenTagPicker,
  onRename,
  onMarkRecurring,
  onOpenRefundLink,
  isCategorySaving = false,
  isTagSaving = false,
  isRecurringSaving = false,
  isRefundSaving = false,
  categoryIndex,
}: Props) {
  const formatCurrency = useFormatCurrency();
  const handleRename = (event: MouseEvent<HTMLButtonElement>) => { event.stopPropagation(); onRename?.(tx.id); };
  const cat = getCategoryMeta(categoryIndex, tx.categoryId);
  const displayMoney = tx.originalMoney ?? {
    amount: tx.amount,
    currencyCode: tx.currencyCode ?? 'BRL',
  };
  const refundState = getRefundState(tx);
  // Pre-existing neutrality only (transfers, fatura payment). A refund link never
  // grays a row — it keeps its normal amount color; the pill alone signals the link.
  const neutral = isFlowNeutral(tx);
  // Neutrality that is NOT refund-driven (transfers, fatura payment) — those rows
  // can never anchor a refund link; refund-flagged rows still can.
  const baseNeutral = (tx.isCashFlowNeutral ?? tx.isInternalTransfer) === true
    && refundState === 'none';
  const { date: dt, hasTime } = transactionDate(tx.date);
  const fatura = tx.faturaId && faturaById ? faturaById.get(tx.faturaId) : undefined;
  const hasFatura = !!(fatura && onOpenFatura);
  const alreadyRecurring = tx.source.isRecurring === true || !!tx.source.recurringPaymentId;
  const canMarkRecurring = tx.source.isExpense === true;
  const paymentLabel = PAYMENT_LABELS[tx.paymentType] ?? null;
  const PaymentIcon = PAYMENT_ICONS[tx.paymentType] ?? ReceiptIcon;

  const title = (
    <TransactionTitle
      adornments={
        <>
          {tx.renamed && (
            <PencilSimpleIcon
              data-testid="renamed-hint"
              className="size-3 shrink-0 text-text-tertiary"
              aria-label="Nome personalizado"
            />
          )}
          {tx.installment && (
            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-2xs text-text-secondary tabular-nums">
              {tx.installment.current}/{tx.installment.total}
            </span>
          )}
        </>
      }
    >
      {tx.merchant}
    </TransactionTitle>
  );

  // Collapsed-row metadata. Responsive: on mobile the category sits on its
  // own full-width line with the fatura/payment affiliation below it (so a
  // long category is never cut by the affiliation); from sm: up it collapses
  // back to the denser single inline line that reads better on desktop.
  const meta = (
    <TransactionMeta>
      <TransactionMetaItem>
        <TransactionCategoryLabel meta={cat} />
      </TransactionMetaItem>
      {(hasFatura || paymentLabel) && (
        <TransactionMetaItem separated>
          {hasFatura ? (
            <FaturaAffiliationPill fatura={fatura!} onOpen={onOpenFatura!} />
          ) : (
            <span className="truncate">{paymentLabel}</span>
          )}
        </TransactionMetaItem>
      )}
      {tx.tags && tx.tags.length > 0 && (
        <TransactionMetaItem separated className="gap-1">
          {tx.tags.slice(0, MAX_ROW_TAG_PILLS).map((tag) => (
            <TagPill key={tag.id} tag={tag} />
          ))}
          {tx.tags.length > MAX_ROW_TAG_PILLS && (
            <TagOverflowPill count={tx.tags.length - MAX_ROW_TAG_PILLS} />
          )}
        </TransactionMetaItem>
      )}
      {/* Its own meta item — self-start so on the mobile flex-col it hugs its
          content on a fresh line instead of stretching or squeezing the
          payment label; inline from sm. */}
      <RefundPill state={refundState} className="self-start sm:self-auto" />
    </TransactionMeta>
  );

  const amountEl = (
    <TransactionAmount
      value={displayMoney.amount}
      currency={displayMoney.currencyCode}
      muted={neutral}
      sub={tx.originalMoney && `${formatCurrency(tx.amount, { currency: tx.currencyCode ?? 'BRL' })} na conta`}
    />
  );

  const caretEl = (
    <CaretDownIcon
      className={cn('size-4 shrink-0 text-text-tertiary transition-transform', expanded && 'rotate-180')}
      weight="bold"
      aria-hidden
    />
  );

  return (
    <Collapsible open={expanded} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <TransactionLine
          as="button"
          className={TRANSACTION_LINE_INTERACTIVE}
          aria-expanded={expanded}
          leading={<CategoryTile meta={cat} />}
          title={title}
          meta={meta}
          amount={amountEl}
          trailing={caretEl}
        >
          <SpaceTransactionMarker transactionId={tx.id} />
        </TransactionLine>
      </CollapsibleTrigger>

      <CollapsibleContent className="px-4 pt-1 pb-3">
        <div className="rounded-xl bg-surface-sunken p-3 text-xs">
          {/* The NAME is the click target (VER-377): PostHog shows dead clicks landing
              on descriptor text, so clicking it opens the detail drawer already in
              rename mode — the pencil makes the affordance visible. */}
          {onRename ? (
            <button
              type="button"
              data-testid={`panel-name-${tx.id}`}
              title="Clique para renomear"
              onClick={(e) => {
                e.stopPropagation();
                onRename(tx.id);
              }}
              className="mb-2 flex max-w-full items-center gap-1.5 text-left"
            >
              <span className="min-w-0 text-sm font-semibold wrap-break-word text-text-primary">{tx.merchant}</span>
              <PencilSimpleIcon className="size-3 shrink-0 text-text-tertiary" aria-hidden />
            </button>
          ) : (
            <p className="mb-2 text-sm font-semibold wrap-break-word text-text-primary">{tx.merchant}</p>
          )}
          {(() => {
            const acc = tx.source.account;
            const connectorImg = acc?.item?.connectorImage;
            const connectorName = acc?.item?.connectorName;
            const last4 = (acc?.number ?? '').replace(/\D/g, '').slice(-4);
            const typeLabel = acc?.type === 'CREDIT' ? 'Crédito' : acc?.type === 'BANK' ? 'Conta corrente' : null;
            const accountInner = (
              <>
                {connectorImg ? (
                  <img
                    src={connectorImg}
                    alt={connectorName ?? ''}
                    className="size-4 shrink-0 rounded-[4px] object-cover"
                  />
                ) : (
                  <BankIcon className="size-4 shrink-0" weight="duotone" />
                )}
                <span className="truncate">{acc?.name ?? 'Conta'}</span>
                {last4 && (
                  <span className="font-mono text-2xs text-text-tertiary tabular-nums">
                    •••• {last4}
                  </span>
                )}
                {typeLabel && (
                  <>
                    <span className="text-text-tertiary">·</span>
                    <span>{typeLabel}</span>
                  </>
                )}
              </>
            );
            const dateInner = (
              <>
                <CalendarIcon className="size-4" weight="duotone" />
                <span className="font-mono tabular-nums">
                  {dt.toLocaleDateString('pt-BR')}
                  {hasTime ? ` ${dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : null}
                </span>
              </>
            );

            // ── Shared action handlers / elements (logic identical across options) ──
            const onCategorize = onOpenCategoryPicker
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onOpenCategoryPicker(tx.id);
                }
              : undefined;
            const onRecurring = onMarkRecurring
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onMarkRecurring(tx.id);
                }
              : undefined;
            const onRefundLink = onOpenRefundLink
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onOpenRefundLink(tx.id);
                }
              : undefined;
            const onDetails = (e: React.MouseEvent) => {
              e.stopPropagation();
              onOpenDetails(tx.id);
            };
            const onTags = onOpenTagPicker
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onOpenTagPicker(tx.id);
                }
              : undefined;
            const showRecurring = onRecurring && canMarkRecurring && !alreadyRecurring;
            // Refund links are anchored on the EXPENSE that was refunded — never initiated from
            // an inflow. The estorno (income) side is linked from the expense or via review.
            // An already-linked expense keeps the affordance while part of it is still
            // uncovered, so a second partial estorno can be attached.
            const refundAmount = reportingAmount(tx);
            const showRefundLink = onRefundLink && hasReportingAmount(tx) && !baseNeutral
              && refundAmount != null && refundAmount < 0
              && uncoveredAmount(tx) > 0;

            // Current hand-rolled pills (Option 1 baseline)
            const pillCategorize = onCategorize && (
              <button
                type="button"
                onClick={onCategorize}
                disabled={isCategorySaving}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium text-text-primary hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Categorizar transação"
              >
                {isCategorySaving ? (
                  <CircleNotchIcon className="size-3 animate-spin" weight="bold" />
                ) : (
                  <TagIcon className="size-3" weight="bold" />
                )}
                Categorizar
              </button>
            );
            // HashIcon, not TagIcon — Categorizar owns the tag glyph; "#" is the
            // tags motif (VER-378).
            const pillTags = onTags && (
              <button
                type="button"
                data-testid={`action-tags-${tx.id}`}
                onClick={onTags}
                disabled={isTagSaving}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium text-text-primary hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Editar tags da transação"
              >
                {isTagSaving ? (
                  <CircleNotchIcon className="size-3 animate-spin" weight="bold" />
                ) : (
                  <HashIcon className="size-3" weight="bold" />
                )}
                Tags
              </button>
            );
            const pillRecurring = showRecurring && (
              <button
                type="button"
                onClick={onRecurring}
                disabled={isRecurringSaving}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium text-text-primary hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Marcar transação como recorrente"
              >
                {isRecurringSaving ? (
                  <CircleNotchIcon className="size-3 animate-spin" weight="bold" />
                ) : (
                  <RepeatIcon className="size-3" weight="bold" />
                )}
                Marcar recorrente
              </button>
            );
            const pillRefundLink = showRefundLink && (
              <button
                type="button"
                onClick={onRefundLink}
                disabled={isRefundSaving}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium text-text-primary hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Vincular reembolso"
              >
                {isRefundSaving ? (
                  <CircleNotchIcon className="size-3 animate-spin" weight="bold" />
                ) : (
                  <ArrowUUpLeftIcon className="size-3" weight="bold" />
                )}
                Vincular reembolso
              </button>
            );
            // A single "Detalhes" action (VER-358): the drawer holds far more than the
            // payment method now (estabelecimento, canal, local da compra), so labeling
            // its entry "Pix"/"Crédito" undersold it — and read as a payment action.
            const pillDetails = (
              <button
                type="button"
                onClick={onDetails}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium text-text-primary hover:bg-surface-sunken"
                aria-label="Ver detalhes da transação"
              >
                <InfoIcon className="size-3" weight="bold" />
                Detalhes
                <CaretRightIcon className="size-2.5 text-text-tertiary" weight="bold" />
              </button>
            );

            return (
              <>
                <div className="flex items-center gap-2 text-muted-foreground">{accountInner}</div>
                <div className="mt-1.5 flex items-center gap-2 text-muted-foreground">{dateInner}</div>
                {/* The payment method stays visible as DATA — it stopped being a button
                    label, and on fatura rows the fatura pill occupies its collapsed-row
                    slot, so without this line "Crédito" would vanish from those rows. */}
                {paymentLabel && (
                  <div className="mt-1.5 flex items-center gap-2 text-muted-foreground">
                    <PaymentIcon className="size-4" weight="duotone" />
                    <span>{paymentLabel}</span>
                  </div>
                )}
                {tx.notes && <p className="mt-2 text-foreground/70">{tx.notes}</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  {pillCategorize}
                  {pillTags}
                  <TransactionRenamePill onRename={onRename ? handleRename : undefined} />
                  <SpaceSendAction transaction={tx.source} />
                  {pillRecurring}
                  {pillRefundLink}
                  {pillDetails}
                </div>
              </>
            );
          })()}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function TransactionRenamePill({ onRename }: { onRename?: (event: MouseEvent<HTMLButtonElement>) => void }) {
  if (!onRename) return null;
  return <button type="button" onClick={onRename} className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-primary hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-ring"><PencilSimpleIcon className="size-3" aria-hidden />Renomear</button>;
}
